import { FetchKey, useFetchFacets } from '@app/src/api/fetchHooks'
import type {
  FilterFacetSelectProps,
  SingleModeProps,
} from '@app/src/pages/ResourceCollection/Filters/FilterFacetSelect'
import FilterFacetSelect from '@app/src/pages/ResourceCollection/Filters/FilterFacetSelect'
import type { Sort } from '@app/src/types/filter'
import { SortOrder } from '@app/src/types/filter'
import { comparePeriods } from '@app/src/utils/getOrderedPeriods'
import sortDistinctPeriodLabels from '@app/src/utils/sortDistinctPeriodLabels'
import React, { useEffect, useMemo } from 'react'
import { useFiltersContext } from './Filters'

type PreFetchedSingleFilterFacetProps = {
  endpoint: string
  onChange: SingleModeProps['onChange']
  queueAfterFilters?: string[]
  sort?: Sort
  facetsName?: string
  isEnum?: boolean
  preSelectedValue?: string | number
} & Omit<FilterFacetSelectProps, 'facetsParam' | 'multiple' | 'onChange'>

const PreFetchedSingleFilterFacet: React.FC<PreFetchedSingleFilterFacetProps> = ({
  endpoint,
  filterName,
  facetsName,
  implicitFilters = [],
  queueAfterFilters,
  value,
  fieldLabel,
  sort,
  isEnum,
  preSelectedValue,
  onChange,
  ...props
}) => {
  const { apiFilters } = useFiltersContext()

  const flatValue = [value].flat()
  const queuedAfterFilters = useMemo(
    () => queueAfterFilters?.every(filter => apiFilters.find(f => f.name === filter)),
    [apiFilters, queueAfterFilters],
  )

  const facetsNameToFetch = facetsName || filterName

  const {
    facets: [facetData],
    refetch,
  } = useFetchFacets({
    key: [FetchKey.FilterFacet, facetsNameToFetch],
    endpoint,
    facetsParam: [{ name: facetsNameToFetch, isEnum }],
    filter: [...implicitFilters, ...apiFilters.filter(filter => filter.name !== filterName)].flat(),
    sort,
    options: { enabled: !queueAfterFilters?.length },
  })

  useEffect(() => {
    if (!facetData?.length) return

    if (preSelectedValue) {
      const preselectedFacet = facetData.find(
        facet => facet.label.toString() === preSelectedValue.toString() || facet.label === preSelectedValue,
      )

      if (preselectedFacet) {
        onChange(preselectedFacet.value.toString())
        return
      }
    }

    const sortedPeriods = sortDistinctPeriodLabels(facetData, SortOrder.DESCENDING)

    const currentValueExists =
      flatValue?.[0] &&
      facetData.some(
        facet =>
          facet.value.toString() === flatValue[0].toString() || facet.label.toString() === flatValue[0].toString(),
      )

    if (!flatValue?.[0] || !currentValueExists) {
      onChange(sortedPeriods[0])
    }
  }, [facetData, flatValue, preSelectedValue, onChange])

  useEffect(() => {
    if (queuedAfterFilters) {
      refetch()
    }
  }, [queuedAfterFilters])

  return (
    <FilterFacetSelect
      implicitFilters={implicitFilters}
      multiple={false}
      facetsParam={{
        key: [FetchKey.FilterFacet, facetsNameToFetch],
        endpoint,
        facetsParam: [{ name: facetsNameToFetch, isEnum }],
        sort,
        options: { enabled: false },
      }}
      customSort={(a, b) => -comparePeriods(a.label, b.label)}
      filterName={filterName}
      onChange={onChange}
      fieldLabel={fieldLabel}
      value={flatValue?.[0]}
      {...props}
    />
  )
}

export default PreFetchedSingleFilterFacet
